import {
  REGIMES_DE_FATURAMENTO,
  type RegimeDeFaturamento,
  STATUS_DE_CONTA,
  type StatusDaConta,
  TIPOS_DE_CONTA,
  type TipoDeConta,
} from '@locaveri/shared'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Building2,
  CircleCheck,
  CircleSlash,
  Clock,
  EllipsisVertical,
  Pencil,
  Search,
  UserPlus,
  Users,
} from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { type Conta, documentoDaConta, nomeDaConta, useContas } from '@/features/contas/api'
import { DialogoDeConta } from '@/features/contas/dialogo-de-conta'
import { DialogoDeCondutores } from '@/features/contas/dialogo-de-condutores'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { useValorAtrasado } from '@/lib/hooks'
import { formatarDocumentoDeExibicao } from '@/lib/mascaras'
import { usePermissao } from '@/lib/permissoes'

/**
 * Contas de cliente — pessoa física e pessoa jurídica.
 *
 * A conta é QUEM PAGA. Quem faz login é o usuário: numa PF costuma ser a mesma
 * pessoa; numa PJ são os condutores autorizados, vinculados pelo diálogo desta
 * mesma tela.
 */

const TAMANHO_DA_PAGINA = 20

const ROTULO_DE_STATUS: Readonly<Record<StatusDaConta, string>> = {
  ATIVA: 'Ativa',
  PENDENTE: 'Pendente',
  SUSPENSA: 'Suspensa',
  ENCERRADA: 'Encerrada',
}

const ROTULO_DE_REGIME: Readonly<Record<RegimeDeFaturamento, string>> = {
  PRE_PAGO: 'Pré-pago',
  FATURADO_MENSAL: 'Faturado mensal',
}

const ROTULO_DE_TIPO: Readonly<Record<TipoDeConta, string>> = {
  PF: 'Pessoa física',
  PJ: 'Pessoa jurídica',
}

const VARIANTE_DE_STATUS = {
  ATIVA: 'success',
  PENDENTE: 'warning',
  SUSPENSA: 'danger',
  ENCERRADA: 'neutral',
} as const

const ICONE_DE_STATUS = {
  ATIVA: CircleCheck,
  PENDENTE: Clock,
  SUSPENSA: CircleSlash,
  ENCERRADA: CircleSlash,
} as const

function BadgeDeStatusDaConta({ status }: { readonly status: StatusDaConta }): ReactElement {
  const Icone = ICONE_DE_STATUS[status]

  return (
    <Badge variant={VARIANTE_DE_STATUS[status]}>
      <Icone aria-hidden="true" />
      {ROTULO_DE_STATUS[status]}
    </Badge>
  )
}

export function ContasPage(): ReactElement {
  const podeCriar = usePermissao('account.create')
  const podeEditar = usePermissao('account.update')

  const [pagina, setPagina] = useState(1)
  const [buscaDigitada, setBuscaDigitada] = useState('')
  const [tipo, setTipo] = useState<TipoDeConta | ''>('')
  const [status, setStatus] = useState<StatusDaConta | ''>('')
  const [regime, setRegime] = useState<RegimeDeFaturamento | ''>('')
  const [dialogoAberto, setDialogoAberto] = useState(false)
  const [emEdicao, setEmEdicao] = useState<Conta | undefined>(undefined)
  const [contaDosCondutores, setContaDosCondutores] = useState<Conta | null>(null)

  const busca = useValorAtrasado(buscaDigitada)

  const filtros = useMemo(
    () => ({
      pagina,
      tamanho: TAMANHO_DA_PAGINA,
      busca,
      tipo,
      status,
      regimeDeFaturamento: regime,
    }),
    [pagina, busca, tipo, status, regime],
  )

  const consulta = useContas(filtros)
  const temFiltro = busca.length > 0 || tipo !== '' || status !== '' || regime !== ''

  function abrirCriacao(): void {
    setEmEdicao(undefined)
    setDialogoAberto(true)
  }

  const colunas = useMemo<ColumnDef<Conta>[]>(
    () => [
      {
        id: 'nome',
        header: 'Cliente',
        accessorFn: (linha: Conta) => nomeDaConta(linha),
        cell: ({ row }) => {
          const conta: Conta = row.original

          return (
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground">{nomeDaConta(conta)}</p>
              <p className="truncate text-xs tabular-money text-fg-secondary">
                {formatarDocumentoDeExibicao(conta.tipo, documentoDaConta(conta))}
              </p>
            </div>
          )
        },
      },
      {
        id: 'tipo',
        header: 'Tipo',
        enableSorting: false,
        cell: ({ row }) => {
          const conta: Conta = row.original

          return <Badge variant="outline">{ROTULO_DE_TIPO[conta.tipo]}</Badge>
        },
      },
      {
        id: 'status',
        header: 'Situação',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeStatusDaConta status={row.original.status} />,
      },
      {
        id: 'faturamento',
        header: 'Faturamento',
        enableSorting: false,
        cell: ({ row }) => {
          const conta: Conta = row.original

          return (
            <div className="min-w-0">
              <p className="text-sm text-foreground">
                {ROTULO_DE_REGIME[conta.regimeDeFaturamento]}
              </p>
              {conta.regimeDeFaturamento === 'FATURADO_MENSAL' ? (
                <p className="text-xs tabular-money text-fg-secondary">
                  Limite {formatarMoedaDoWire(conta.limiteDeCredito)}
                  {conta.diaDeFechamento === null
                    ? ''
                    : ` · fecha dia ${String(conta.diaDeFechamento)}`}
                </p>
              ) : null}
            </div>
          )
        },
      },
      {
        id: 'condutores',
        header: 'Condutores',
        enableSorting: false,
        cell: ({ row }) => {
          const conta: Conta = row.original

          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setContaDosCondutores(conta)
              }}
            >
              <Users aria-hidden="true" />
              <span className="tabular-money">{conta.quantidadeDeCondutores}</span>
            </Button>
          )
        },
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const conta: Conta = row.original

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Ações de ${nomeDaConta(conta)}`}
                  className="size-8"
                >
                  <EllipsisVertical aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onSelect={() => {
                    setContaDosCondutores(conta)
                  }}
                >
                  <Users aria-hidden="true" />
                  Condutores autorizados
                </DropdownMenuItem>

                {podeEditar ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      setEmEdicao(conta)
                      setDialogoAberto(true)
                    }}
                  >
                    <Pencil aria-hidden="true" />
                    Editar conta
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      },
    ],
    [podeEditar],
  )

  const dados = consulta.data

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Clientes</h1>
          <p className="text-sm text-fg-secondary">
            Contas que pagam e assinam o contrato — pessoa física e pessoa jurídica.
          </p>
        </div>

        {podeCriar ? (
          <Button onClick={abrirCriacao}>
            <UserPlus aria-hidden="true" />
            Nova conta
          </Button>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5">
          <Label htmlFor="filtro-busca-contas">Buscar</Label>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2
                text-fg-muted"
            />
            <Input
              id="filtro-busca-contas"
              type="search"
              value={buscaDigitada}
              onChange={(evento) => {
                setPagina(1)
                setBuscaDigitada(evento.target.value)
              }}
              placeholder="Nome, documento ou e-mail"
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-tipo">Tipo</Label>
          <Select
            id="filtro-tipo"
            value={tipo}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setTipo(TIPOS_DE_CONTA.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todos</option>
            {TIPOS_DE_CONTA.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DE_TIPO[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-status-conta">Situação</Label>
          <Select
            id="filtro-status-conta"
            value={status}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setStatus(STATUS_DE_CONTA.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todas</option>
            {STATUS_DE_CONTA.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DE_STATUS[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-regime">Faturamento</Label>
          <Select
            id="filtro-regime"
            value={regime}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setRegime(REGIMES_DE_FATURAMENTO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todos</option>
            {REGIMES_DE_FATURAMENTO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DE_REGIME[item]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <TabelaDeDados
        colunas={colunas}
        dados={dados?.itens ?? []}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Contas de cliente"
        estadoVazio={
          <EmptyState
            icone={Building2}
            titulo={temFiltro ? 'Nenhuma conta com esses filtros' : 'Nenhuma conta cadastrada'}
            descricao={
              temFiltro
                ? 'Ajuste a busca ou os filtros para ver outros resultados.'
                : 'Cadastre o primeiro cliente. Empresas podem ter vários condutores autorizados.'
            }
            acao={
              temFiltro ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setBuscaDigitada('')
                    setTipo('')
                    setStatus('')
                    setRegime('')
                    setPagina(1)
                  }}
                >
                  Limpar filtros
                </Button>
              ) : podeCriar ? (
                <Button onClick={abrirCriacao}>
                  <UserPlus aria-hidden="true" />
                  Nova conta
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={setPagina}
        desabilitado={consulta.isFetching}
      />

      <DialogoDeConta
        aberto={dialogoAberto}
        conta={emEdicao}
        aoFechar={() => {
          setDialogoAberto(false)
        }}
      />

      <DialogoDeCondutores
        conta={contaDosCondutores}
        podeConvidar={podeEditar}
        aoFechar={() => {
          setContaDosCondutores(null)
        }}
      />
    </div>
  )
}
